import { z } from "zod";
import { isDeployable } from "@lms/backend/lib/deploy/deployable";

/** Dev/test seed password — satisfies the length/character rules below */
export const DEV_PASSWORD = "Password1234";

/** SEC-3: API error code for choosing the published demo password in production. */
export const DEMO_PASSWORD_REFUSED = "DEMO_PASSWORD_REFUSED";

/**
 * SEC-3: production refuses `DEV_PASSWORD` at sign-in (R3-S2, `password-check.ts`), so it must
 * also be refused whenever a password is chosen, or the user is locked out right after.
 * Read at parse time so the environment of the running server decides.
 */
export function isRefusedNewPassword(
  password: string,
  env: { NODE_ENV?: string; DEPLOYABLE?: string } = process.env,
): boolean {
  return isDeployable(env) && password === DEV_PASSWORD;
}

/** NIST-aligned password rules — decisions.md §2 */
export const passwordSchema = z
  .string()
  .min(12, "Password must be at least 12 characters")
  .regex(/[A-Z]/, "Password must contain an uppercase letter")
  .regex(/[a-z]/, "Password must contain a lowercase letter")
  .regex(/[0-9]/, "Password must contain a number")
  .refine((password) => !isRefusedNewPassword(password), {
    message: "This password is publicly known and can't be used. Choose a different one.",
    params: { apiCode: DEMO_PASSWORD_REFUSED },
  });
